import React, { useState, useEffect } from 'react';

// ── Keyframes (injected once) ─────────────────────────────────────────────────
let _kfDone = false;
function injectKF() {
  if (_kfDone || document.getElementById('cpp-kf')) return;
  _kfDone = true;
  const s = document.createElement('style');
  s.id = 'cpp-kf';
  s.textContent = `
    @keyframes cppSlideUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  `;
  document.head.appendChild(s);
}

const TRAIT_ORDER  = ['VISUAL', 'ESTRUTURADO', 'CONVERSACIONAL', 'PRÁTICO'];
const TRAIT_LABELS = { VISUAL:'Visual', ESTRUTURADO:'Estruturado', CONVERSACIONAL:'Conversacional', 'PRÁTICO':'Prático' };

// ── Score bar row ─────────────────────────────────────────────────────────────
function ScoreBar({ label, barData, isPrimary }) {
  const { score, color } = barData;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <div style={{
        width: 108, fontSize: 12, flexShrink: 0,
        color:      isPrimary ? color : 'rgba(148,163,184,0.65)',
        fontWeight: isPrimary ? 500 : 400,
      }}>
        {label}
      </div>
      <div style={{ flex: 1, display: 'flex', gap: 2.5 }}>
        {Array.from({ length: 10 }, (_, i) => (
          <div key={i} style={{
            flex: 1, height: 5, borderRadius: 2,
            background: i < score
              ? (isPrimary ? color : `${color}70`)
              : 'rgba(255,255,255,0.055)',
          }} />
        ))}
      </div>
      <span style={{
        fontSize: 11, width: 14, textAlign: 'right', flexShrink: 0,
        color: isPrimary ? color : 'rgba(100,116,139,0.5)',
      }}>
        {score}
      </span>
    </div>
  );
}

// ── Drift suggestion toast ────────────────────────────────────────────────────
export function DriftSuggestionToast({ suggestion, accentColor, onAccept, onDismiss }) {
  useEffect(() => { injectKF(); }, []);
  if (!suggestion) return null;

  const accent = accentColor || '#38bdf8';
  return (
    <div style={{
      position: 'fixed', bottom: 86, left: '50%', transform: 'translateX(-50%)',
      zIndex: 850, width: 'min(360px, calc(100vw - 48px))',
      background: 'rgba(6,12,38,0.97)', backdropFilter: 'blur(14px)',
      border: `1px solid ${accent}35`, borderRadius: 14,
      padding: '14px 18px', boxShadow: '0 8px 36px rgba(0,0,0,0.6)',
      animation: 'cppSlideUp .32s ease',
      fontFamily: '-apple-system,BlinkMacSystemFont,"Inter","Segoe UI",sans-serif',
    }}>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: 'rgba(172,196,236,0.85)', lineHeight: 1.6 }}>
        {suggestion.message}
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onAccept} style={{
          flex: 1, padding: '8px 0', fontSize: 12.5,
          background: `${accent}18`, border: `1px solid ${accent}40`,
          color: accent, borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
        }}>Sim</button>
        <button onClick={onDismiss} style={{
          flex: 1, padding: '8px 0', fontSize: 12.5,
          background: 'none', border: '1px solid rgba(255,255,255,0.08)',
          color: 'rgba(100,116,139,0.65)', borderRadius: 8,
          cursor: 'pointer', fontFamily: 'inherit',
        }}>Agora não</button>
      </div>
    </div>
  );
}

// ── Profile panel ─────────────────────────────────────────────────────────────
export function CognitiveProfilePanel({ profile, workspaceProfile, onClose, isMobile }) {
  useEffect(() => { injectKF(); }, []);
  if (!profile) return null;

  const bars        = workspaceProfile?.bars || {};
  const accent      = workspaceProfile?.accentColor || '#38bdf8';
  const primary     = workspaceProfile?.primaryTrait;
  const sessions    = profile?.cognitive?.behavior?.sessions || 0;
  const createdAt   = profile?.cognitive?.createdAt
    ? new Date(profile.cognitive.createdAt).toLocaleDateString('pt-BR', { day:'2-digit', month:'short', year:'numeric' })
    : null;

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:840, background:'rgba(0,0,0,0.22)', backdropFilter:'blur(1px)' }} />

      {/* Panel */}
      <div style={{
        position: 'fixed',
        right:  isMobile ? 10 : 18,
        bottom: isMobile ? 76 : 70,
        zIndex: 841,
        width:  isMobile ? 'calc(100vw - 20px)' : 276,
        background: 'rgba(5,11,36,0.98)', backdropFilter: 'blur(18px)',
        border: `1px solid ${accent}25`, borderRadius: 16,
        boxShadow: '0 14px 52px rgba(0,0,0,0.7)',
        fontFamily: '-apple-system,BlinkMacSystemFont,"Inter","Segoe UI",sans-serif',
        animation: 'cppSlideUp .28s ease',
        overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'14px 16px 11px',
          borderBottom: `1px solid ${accent}16`,
        }}>
          <div>
            <div style={{ fontSize:10, color:`${accent}80`, letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:3 }}>
              Perfil Cognitivo
            </div>
            <div style={{ fontSize:15.5, fontWeight:400, color:'#eef5ff' }}>{profile.name}</div>
          </div>
          <button onClick={onClose} style={{
            width:26, height:26, borderRadius:'50%', border:'none',
            background:'rgba(255,255,255,0.06)', color:'rgba(148,163,184,0.55)',
            cursor:'pointer', fontSize:14, lineHeight:1,
            display:'flex', alignItems:'center', justifyContent:'center',
          }}>×</button>
        </div>

        {/* Bars */}
        <div style={{ padding:'14px 16px 10px' }}>
          {profile.cognitive ? (
            TRAIT_ORDER.map(trait => {
              const barData = bars[trait];
              if (!barData) return null;
              return (
                <ScoreBar
                  key={trait}
                  label={TRAIT_LABELS[trait]}
                  barData={barData}
                  isPrimary={trait === primary}
                />
              );
            })
          ) : (
            <div style={{ fontSize:12.5, color:'rgba(100,116,139,0.55)', textAlign:'center', padding:'4px 0 8px' }}>
              Este perfil ainda não passou pelo questionário cognitivo.
            </div>
          )}
        </div>

        {/* Footer */}
        {(createdAt || sessions > 0) && (
          <div style={{
            display:'flex', justifyContent:'space-between', alignItems:'center',
            padding:'8px 16px 12px',
            borderTop: `1px solid ${accent}10`,
          }}>
            {createdAt && (
              <span style={{ fontSize:10, color:'rgba(100,116,139,0.45)' }}>Criado {createdAt}</span>
            )}
            {sessions > 0 && (
              <span style={{ fontSize:10, color:'rgba(100,116,139,0.4)' }}>
                {sessions} sess{sessions === 1 ? 'ão' : 'ões'}
              </span>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ── Trigger button (fixed, bottom-right, only in Foco Total) ──────────────────
export function CognitiveProfileButton({ onClick, accentColor, isMobile }) {
  const [hov, setHov] = useState(false);
  const accent = accentColor || '#38bdf8';

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      title="Perfil Cognitivo"
      style={{
        position: 'fixed',
        bottom:   isMobile ? 74 : 22,
        right:    isMobile ? 10 : 18,
        zIndex:   830,
        display:  'flex', alignItems:'center', gap:6,
        padding:  isMobile ? '7px 11px' : '7px 13px',
        background: hov ? `${accent}18` : 'rgba(5,11,36,0.88)',
        border: `1px solid ${hov ? accent+'48' : accent+'1e'}`,
        borderRadius: 10, backdropFilter: 'blur(10px)',
        color:     hov ? accent : 'rgba(100,116,139,0.55)',
        fontSize:  11, letterSpacing:'0.04em',
        cursor:    'pointer', fontFamily:'inherit',
        transition:'all .2s ease',
        boxShadow: hov ? `0 0 16px ${accent}18` : 'none',
        userSelect:'none',
      }}
    >
      {/* Person icon */}
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink:0 }}>
        <circle cx="6" cy="3.5" r="2.2" stroke="currentColor" strokeWidth="1"/>
        <path d="M 1.5 11 C 1.5 8.5 3.5 7 6 7 C 8.5 7 10.5 8.5 10.5 11"
          stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
      </svg>
      {!isMobile ? 'Perfil Cognitivo' : null}
    </button>
  );
}
